import { useEffect } from "react";

/**
 * useModuleTabClose
 *
 * Notifies the SSO parent window when this module tab is closed or unloaded.
 * Uses window.opener.postMessage to send a MODULE_CLOSE event back to SSO,
 * allowing SSO to update its active module registry.
 *
 * ⚠️ KNOWN LIMITATION:
 * beforeunload + postMessage is best-effort for cross-origin communication.
 * It works in most desktop browser scenarios but is not guaranteed during:
 *   - Browser/tab crashes
 *   - Mobile browser tab close
 *   - Force kill via OS task manager
 *
 * Long-term fix: SSO should poll newTab.closed on the window reference
 * it holds after window.open() — that is the only 100% reliable approach.
 *
 * @param moduleId - The module ID to send in the close message (falls back to sessionStorage)
 */

export function useModuleTabClose(moduleId?: string): void {
  useEffect(() => {
    const handleTabClose = (): void => {
      const resolvedModuleId =
        moduleId ?? sessionStorage.getItem("loginModuleId");
      const ssoUrl = sessionStorage.getItem("SSOUrl");

      if (!resolvedModuleId || !ssoUrl || !window.opener) return;

      try {
        window.opener.postMessage(
          {
            type: "MODULE_CLOSE",
            moduleId: resolvedModuleId,
            app: "ZENTIXS",
          },
          ssoUrl
        );
      } catch (_) {
        // Silently fail — cross-origin errors during unload are non-recoverable
      }
    };

    window.addEventListener("beforeunload", handleTabClose);

    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, [moduleId]);
}